/**
 * WHERE IS THIS MATERIAL? - the site-wise distribution panel on Master Data.
 *
 * Asked for 11-09-2026. Next to every material in the list sits a small button
 * showing how many sites are involved with it ("3 sites"). Pressing it opens a
 * panel - on the right on a desktop, sliding up from the bottom on a phone or
 * tablet - with one card per site:
 *
 *   ON HAND      quantity in the stock unit, approximate value, a bar so the
 *                biggest holding stands out
 *   COMING IN    open purchase-order quantity still due at that site, and
 *                inter-site transfers already on the road to it
 *   ASKED FOR    approved requisition quantity not yet on any order
 *   HEALTH       below-minimum flag and the date of the last movement
 *
 * Only sites that hold, expect or have asked for the material are listed.
 * Tapping a site opens Inventory filtered to that site and material.
 *
 * WHO: Head of Stores, Head of Procurement, Project Head, CMD, CXO and the
 * System Administrator (CAN.viewMaterialDistribution). Every other role sees
 * the Master Data list exactly as before, with no button.
 *
 * READ-ONLY and additive. Four SELECTs, all through existing views and
 * row level security - no database change. The data is loaded ONCE when the
 * Master Data screen opens (so every row's count is ready) and re-read each
 * time the panel is opened, so the panel is never stale.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AlertTriangle, ArrowRight, ClipboardList, MapPin, PackageCheck, Truck, X } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { ddmmyyyy, qty, rupees } from '@/lib/format'

// deno-lint-ignore no-explicit-any
type Any = any

/** Open purchase orders: approved and not yet fully received. */
const OPEN_PO = ['APPROVED', 'VENDIN', 'VENDACK', 'PARTIALLY_RECEIVED']
/** Transfers that have left the sending store and not yet arrived. */
const ON_THE_ROAD = ['TRANSFER_INITIATED', 'IN_TRANSIT']

export interface SiteLine {
  site_id: string
  site_name: string
  on_hand: number
  value: number | null
  rate_source: string | null
  below_minimum: boolean
  min_level: number | null
  last_txn_at: string | null
  po_due: number
  po_ids: string[]
  transit_in: number
  transit_from: string[]
  asked: number
  pr_ids: string[]
}

interface Index {
  /** material_id -> site_id -> line */
  byMaterial: Map<string, Map<string, SiteLine>>
  loadedAt: number
}

const blank = (site_id: string, site_name: string): SiteLine => ({
  site_id, site_name, on_hand: 0, value: null, rate_source: null, below_minimum: false,
  min_level: null, last_txn_at: null, po_due: 0, po_ids: [], transit_in: 0, transit_from: [],
  asked: 0, pr_ids: [],
})

/**
 * Every row, a page at a time. The API hands back at most 1,000 rows per call;
 * with 37 sites the stock position will pass that as materials are added, and
 * a silently truncated list would show a site count that is quietly wrong.
 */
async function all(build: () => Any): Promise<{ data: Any[]; error: { message: string } | null }> {
  const out: Any[] = []
  for (let from = 0; ; from += 1000) {
    const { data, error } = await build().range(from, from + 999)
    if (error) return { data: out, error }
    out.push(...(data ?? []))
    if (!data || data.length < 1000) return { data: out, error: null }
  }
}

async function loadIndex(siteNames: Map<string, string>): Promise<Index> {
  const [stock, po, ist, pr] = await Promise.all([
    all(() => supabase.from('v_stock_position')
      .select('site_id, site_name, material_id, quantity_on_hand, approx_value, rate_source, below_minimum, min_stock_level, last_txn_at')
      .neq('quantity_on_hand', 0).order('site_id').order('material_id')),
    all(() => supabase.from('po_line_items')
      .select('id, material_id, quantity_remaining, po_id, purchase_orders!inner(site_id, status)')
      .gt('quantity_remaining', 0)
      .in('purchase_orders.status', OPEN_PO).order('id')),
    all(() => supabase.from('ist_line_items')
      .select('id, material_id, quantity, ist_id, inter_site_transfers!inner(to_site_id, from_site_id, status)')
      .in('inter_site_transfers.status', ON_THE_ROAD).order('id')),
    all(() => supabase.from('v_open_pr_lines')
      .select('pr_line_id, material_id, site_id, quantity_open, pr_id')
      .gt('quantity_open', 0).order('pr_line_id')),
  ])
  for (const r of [stock, po, ist, pr]) if (r.error) throw new Error(r.error.message)

  const byMaterial = new Map<string, Map<string, SiteLine>>()
  const line = (material: string, site: string) => {
    let m = byMaterial.get(material)
    if (!m) { m = new Map(); byMaterial.set(material, m) }
    let l = m.get(site)
    if (!l) { l = blank(site, siteNames.get(site) ?? site); m.set(site, l) }
    return l
  }

  for (const s of (stock.data ?? []) as Any[]) {
    const l = line(s.material_id, s.site_id)
    l.site_name = s.site_name ?? l.site_name
    l.on_hand = Number(s.quantity_on_hand) || 0
    l.value = s.approx_value == null ? null : Number(s.approx_value)
    l.rate_source = s.rate_source ?? null
    l.below_minimum = !!s.below_minimum
    l.min_level = s.min_stock_level == null ? null : Number(s.min_stock_level)
    l.last_txn_at = s.last_txn_at ?? null
  }
  for (const p of (po.data ?? []) as Any[]) {
    const site = p.purchase_orders?.site_id
    if (!site) continue
    const l = line(p.material_id, site)
    l.po_due += Number(p.quantity_remaining) || 0
    if (!l.po_ids.includes(p.po_id)) l.po_ids.push(p.po_id)
  }
  for (const t of (ist.data ?? []) as Any[]) {
    const to = t.inter_site_transfers?.to_site_id
    if (!to) continue
    const l = line(t.material_id, to)
    l.transit_in += Number(t.quantity) || 0
    const from = siteNames.get(t.inter_site_transfers?.from_site_id) ?? t.inter_site_transfers?.from_site_id
    if (from && !l.transit_from.includes(from)) l.transit_from.push(from)
  }
  for (const r of (pr.data ?? []) as Any[]) {
    const l = line(r.material_id, r.site_id)
    l.asked += Number(r.quantity_open) || 0
    if (!l.pr_ids.includes(r.pr_id)) l.pr_ids.push(r.pr_id)
  }
  return { byMaterial, loadedAt: Date.now() }
}

/**
 * The whole distribution, loaded once for the Master Data screen.
 * `enabled` is false for every role without the capability, so nobody else
 * pays for the four queries.
 */
export function useMaterialDistribution(enabled: boolean, sites: { id: string; site_name: string }[]) {
  const [index, setIndex] = useState<Index | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const siteNames = useMemo(() => new Map(sites.map((s) => [s.id, s.site_name])), [sites])

  const refresh = useCallback(async () => {
    if (!enabled) return
    setLoading(true); setError(null)
    try { setIndex(await loadIndex(siteNames)) }
    catch (e) { setError(e instanceof Error ? e.message : String(e)) }
    finally { setLoading(false) }
  }, [enabled, siteNames])

  useEffect(() => { void refresh() }, [refresh])

  const linesFor = useCallback((materialId: string): SiteLine[] =>
    [...(index?.byMaterial.get(materialId)?.values() ?? [])]
      .sort((a, b) => b.on_hand - a.on_hand || b.po_due - a.po_due || b.asked - a.asked
                      || a.site_name.localeCompare(b.site_name)),
  [index])

  const countFor = useCallback((materialId: string) => index?.byMaterial.get(materialId)?.size ?? 0, [index])

  return { loading, error, refresh, linesFor, countFor, ready: !!index }
}

/** The small button that sits next to the pencil / eye on each material row. */
export function DistributionButton({ count, active, ready, onClick }: {
  count: number; active: boolean; ready: boolean; onClick: () => void
}) {
  const none = ready && count === 0
  const label = !ready ? 'Loading' : count === 1 ? '1 site' : `${count} sites`
  return (
    <button
      type="button"
      onClick={onClick}
      // Quiet by default, so a 400-row master list does not turn into a
      // pin-board; it lifts only under the cursor, and goes solid while its
      // panel is open so you can see which row you are looking at.
      className={`inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border
        px-2.5 py-1 text-[11px] font-semibold leading-none transition-colors duration-150
        focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500/50 focus-visible:ring-offset-1
        ${active
          ? 'border-transparent bg-gradient-to-r from-sky-700 to-indigo-700 text-white shadow-sm'
          : none
            ? 'border-slate-200 bg-white text-slate-400 hover:border-slate-300 hover:bg-slate-50'
            : 'border-sky-200/80 bg-gradient-to-r from-sky-50 to-indigo-50 text-sky-800 shadow-sm hover:border-sky-300 hover:from-sky-100 hover:to-indigo-100'}`}
      title={none ? 'No site holds, expects or has asked for this material'
                  : 'Where is this material? — site-wise stock, orders and requisitions'}
      aria-label={`Where is this material — ${label}`}
      aria-pressed={active}
    >
      <MapPin className={`h-3.5 w-3.5 ${active ? 'text-white' : none ? 'text-slate-400' : 'text-sky-600'}`} />
      {!ready ? '…' : (
        <>
          <span className={`rounded-full px-1 tabular-nums ${
            active ? 'bg-white/20' : none ? 'text-slate-400' : 'bg-white/80 text-sky-900'}`}>{count}</span>
          <span className={active ? 'text-white/90' : none ? '' : 'text-sky-700'}>
            {count === 1 ? 'site' : 'sites'}
          </span>
        </>
      )}
    </button>
  )
}

/**
 * The panel itself. Fixed to the right on large screens (the list stays usable
 * beside it - click another row and the panel follows), and a bottom sheet
 * with a backdrop on phones and tablets.
 */
export function MaterialDistributionPanel({ material, lines, loading, onClose, onRefresh }: {
  material: Any
  lines: SiteLine[]
  loading: boolean
  onClose: () => void
  onRefresh: () => void
}) {
  const navigate = useNavigate()
  const uom = material?.unit_of_measure ?? ''

  // Esc closes, on every form factor.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  // Fresh figures every time a material is opened.
  useEffect(() => { onRefresh() }, [material?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const t = useMemo(() => lines.reduce((a, l) => ({
    onHand: a.onHand + l.on_hand,
    value: a.value + (l.value ?? 0),
    holding: a.holding + (l.on_hand > 0 ? 1 : 0),
    incoming: a.incoming + l.po_due + l.transit_in,
    asked: a.asked + l.asked,
    low: a.low + (l.below_minimum && l.on_hand > 0 ? 1 : 0),
  }), { onHand: 0, value: 0, holding: 0, incoming: 0, asked: 0, low: 0 }), [lines])
  const maxOnHand = Math.max(1, ...lines.map((l) => l.on_hand))

  const open = (siteId: string) => {
    onClose()
    navigate(`/inventory?site=${encodeURIComponent(siteId)}&q=${encodeURIComponent(material.item_code ?? material.id)}`)
  }

  return (
    <>
      {/* Backdrop only below the desktop breakpoint - on a desktop the list
          beside the panel stays clickable. */}
      <div className="fixed inset-0 z-40 bg-slate-900/30 lg:hidden" onClick={onClose} aria-hidden />

      <aside
        role="dialog" aria-label={`Site-wise distribution of ${material?.item_name}`}
        className="fixed inset-x-0 bottom-0 z-50 flex max-h-[78vh] flex-col rounded-t-2xl bg-white shadow-2xl
                   lg:inset-x-auto lg:bottom-0 lg:right-0 lg:top-0 lg:max-h-none lg:w-[400px] xl:w-[440px] lg:rounded-none lg:border-l lg:border-slate-200"
      >
        {/* grab handle on the sheet */}
        <div className="mx-auto mt-2 h-1.5 w-12 rounded-full bg-slate-300 lg:hidden" />

        <header className="flex items-start gap-3 border-b border-slate-100 px-4 py-3">
          <div className="min-w-0 flex-1">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-oval-600">Where is it?</p>
            <h2 className="truncate text-base font-bold text-slate-900">{material?.item_name}</h2>
            <p className="font-mono text-xs text-slate-500">{material?.id} · {material?.item_code} · stock unit {uom}</p>
          </div>
          <button className="btn-ghost btn-sm" onClick={onClose} title="Close (Esc)"><X className="h-5 w-5" /></button>
        </header>

        {/* totals */}
        <div className="grid grid-cols-2 gap-2 border-b border-slate-100 px-4 py-3">
          <Tile label="On hand" value={`${qty(t.onHand)} ${uom}`} sub={`${t.holding} site${t.holding === 1 ? '' : 's'}`} />
          <Tile label="Approx. value" value={rupees(t.value)} />
          <Tile label="Coming in" value={`${qty(t.incoming)} ${uom}`} tone={t.incoming > 0 ? 'info' : undefined} />
          <Tile label="Asked for" value={`${qty(t.asked)} ${uom}`} tone={t.asked > 0 ? 'warn' : undefined} />
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3">
          {loading && lines.length === 0 && <p className="py-6 text-center text-sm text-slate-500">Loading…</p>}
          {!loading && lines.length === 0 && (
            <p className="py-8 text-center text-sm text-slate-500">
              No site holds this material, none is expecting it, and nobody has asked for it.
            </p>
          )}
          {t.low > 0 && (
            <p className="mb-3 flex items-center gap-1.5 rounded-lg bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800">
              <AlertTriangle className="h-4 w-4" /> {t.low} site{t.low === 1 ? ' is' : 's are'} below the minimum stock level.
            </p>
          )}

          <ul className="space-y-2">
            {lines.map((l) => (
              <li key={l.site_id}>
                <button type="button" onClick={() => open(l.site_id)}
                        className="group w-full rounded-xl border border-slate-200 p-3 text-left transition hover:border-oval-300 hover:bg-oval-50/40"
                        title={`Open ${l.site_name} in Inventory`}>
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-semibold text-slate-800">{l.site_name}</p>
                    <ArrowRight className="mt-0.5 h-4 w-4 shrink-0 text-slate-300 group-hover:text-oval-600" />
                  </div>

                  {/* on hand + bar */}
                  <div className="mt-1 flex items-baseline justify-between gap-2">
                    <span className={`text-lg font-bold ${l.on_hand > 0 ? 'text-slate-900' : 'text-slate-400'}`}>
                      {qty(l.on_hand)} <span className="text-xs font-medium text-slate-500">{uom}</span>
                    </span>
                    {l.value != null && l.on_hand > 0 && (
                      <span className="text-xs text-slate-600" title={l.rate_source ?? ''}>{rupees(l.value)}</span>
                    )}
                  </div>
                  <div className="mt-1 h-1.5 w-full rounded-full bg-slate-100">
                    <div className="h-1.5 rounded-full bg-oval-500" style={{ width: `${Math.max(0, (l.on_hand / maxOnHand) * 100)}%` }} />
                  </div>

                  {/* chips */}
                  <div className="mt-2 flex flex-wrap gap-1.5 text-[11px]">
                    {l.po_due > 0 && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-sky-50 px-2 py-0.5 text-sky-800"
                            title={l.po_ids.join(', ')}>
                        <PackageCheck className="h-3 w-3" /> {qty(l.po_due)} due on {l.po_ids.length} PO{l.po_ids.length === 1 ? '' : 's'}
                      </span>
                    )}
                    {l.transit_in > 0 && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-violet-50 px-2 py-0.5 text-violet-800">
                        <Truck className="h-3 w-3" /> {qty(l.transit_in)} on the road from {l.transit_from.join(', ')}
                      </span>
                    )}
                    {l.asked > 0 && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-amber-800"
                            title={l.pr_ids.join(', ')}>
                        <ClipboardList className="h-3 w-3" /> {qty(l.asked)} asked on {l.pr_ids.length} PR{l.pr_ids.length === 1 ? '' : 's'}
                      </span>
                    )}
                    {l.below_minimum && l.on_hand > 0 && (
                      <span className="rounded-full bg-red-50 px-2 py-0.5 font-semibold text-red-700">
                        Below min{l.min_level != null ? ` (${qty(l.min_level)})` : ''}
                      </span>
                    )}
                    {l.last_txn_at && (
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-600">
                        Last moved {ddmmyyyy(l.last_txn_at)}
                      </span>
                    )}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </div>

        <footer className="border-t border-slate-100 px-4 py-2 text-[11px] leading-snug text-slate-500">
          Quantities in the stock unit ({uom}). Values are approximate - last purchase rate at the site,
          else elsewhere, else the base rate. Coming in = approved orders not yet received, plus transfers
          on the road. Asked for = approved requisitions not yet on an order. Tap a site to open it in Inventory.
        </footer>
      </aside>
    </>
  )
}

function Tile({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: 'info' | 'warn' }) {
  return (
    <div className={`rounded-lg px-2.5 py-2 ${tone === 'info' ? 'bg-sky-50' : tone === 'warn' ? 'bg-amber-50' : 'bg-slate-50'}`}>
      <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</p>
      <p className="truncate text-sm font-bold text-slate-900">{value}</p>
      {sub && <p className="text-[10px] text-slate-500">{sub}</p>}
    </div>
  )
}
